import React from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import "../styles/management-tables.css";
import "../styles/modal.css";
import "../styles/form.css";
import SideTabs from "../components/layout/SideTabs";
import { useAppContext } from "../context/AppContext";
import { getCompanyNames } from "../services/companyService";
import {
  getTasksByCompanyId,
  getTaskWithRelations,
} from "../services/taskService";
import {
  getProcessWithRelations,
  updateProcess,
  getProcessesByCompanyId,
} from "../services/processService";
import { getProcessCategories } from "../services/processCategoryService";
import { getProcessStatuses } from "../services/processStatusService";
import {
  getKPIsByProcessId,
  getAllKPIs,
  createProcessKPI,
  updateProcessKPI,
  deleteProcessKPI,
} from "../services/kpiService";
import RichTextEditor from "../components/ui/RichTextEditor";
import GatewaySection from "../components/ui/GatewaySection";
import ValueAddedPanel from "../components/ui/ValueAddedPanel";
import PerformancePanel from "../components/ui/PerformancePanel";
import FlowAnalysisPanel from "../components/ui/FlowAnalysisPanel";
import RedesignScenariosPanel from "../components/ui/RedesignScenariosPanel";
import WorkflowJobChips, {
  RaciLegendBar,
  mapJobTasksToWorkflowJobs,
} from "../components/ui/WorkflowJobChips";
import { deleteGateway, clearStoredBpmnXml } from "../services/bpmnService";
import { toast } from "../utils/toast";
import {
  exclusiveSkipsConvergence,
  gatewayRequiresConvergence,
} from "../utils/gatewayConvergence";
import { useLocation, useParams } from "react-router-dom";

const START_EVENT_GATEWAY_ID = "__START_EVENT__";
const normalizeGatewayAfterTaskId = (value) =>
  String(value) === START_EVENT_GATEWAY_ID
    ? null
    : value
      ? Number(value)
      : null;

const ProcessEditPage = () => {
  const { setActiveSection } = useAppContext();
  const { processId: processIdParam } = useParams();
  const location = useLocation();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [companies, setCompanies] = React.useState([]);
  const [processes, setProcesses] = React.useState([]);
  const [processCategories, setProcessCategories] = React.useState([]);
  const [processStatuses, setProcessStatuses] = React.useState([]);
  const [tasks, setTasks] = React.useState([]);
  const [workflowJobs, setWorkflowJobs] = React.useState([]); // per-row jobs for display
  const [gateways, setGateways] = React.useState([]);
  // Track backend IDs of gateways that existed when the page loaded,
  // so we can explicitly DELETE any that the user removes before saving.
  const originalGatewayBackendIdsRef = React.useRef([]);

  // KPI State
  const [processKPIs, setProcessKPIs] = React.useState([]);
  const [availableKPIs, setAvailableKPIs] = React.useState([]);
  const [kpiLoading, setKpiLoading] = React.useState(false);
  const [showKPIModal, setShowKPIModal] = React.useState(false);
  const [kpiModalMode, setKpiModalMode] = React.useState("create"); // 'create' | 'edit'
  const [selectedKPI, setSelectedKPI] = React.useState(null);
  const [kpiForm, setKpiForm] = React.useState({
    kpiType: "TYPE1",
    kpi_id: "",
    custom_kpi_name: "",
    custom_uom: "",
    value: "",
  });
  const [kpiSaving, setKpiSaving] = React.useState(false);

  const [processId, setProcessId] = React.useState(null);
  const [form, setForm] = React.useState({
    process_name: "",
    process_code: "",
    version: 0,
    company_id: "",
    process_status_id: "",
    process_category_id: "",
    parent_process_id: "",
    process_overview: "",
    case_arrival_rate: "",
  });
  const [workflow, setWorkflow] = React.useState([]); // rows: { task_id, job_id }

  // Helper to load tasks for a specific company
  const loadTasksForCompany = async (companyId) => {
    if (!companyId) {
      setTasks([]);
      return;
    }
    try {
      const tasksRes = await getTasksByCompanyId(companyId).catch(() => []);
      setTasks(Array.isArray(tasksRes) ? tasksRes : []);
    } catch {
      setTasks([]);
    }
  };

  const loadProcessesForCompany = async (companyId) => {
    if (!companyId) {
      setProcesses([]);
      return;
    }
    try {
      const processesRes = await getProcessesByCompanyId(companyId).catch(
        () => [],
      );
      setProcesses(Array.isArray(processesRes) ? processesRes : []);
    } catch {
      setProcesses([]);
    }
  };

  const lastFetchKeyRef = React.useRef(null);
  React.useEffect(() => {
    const key = `${processIdParam ?? "__no_param__"}:${location.pathname}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    (async () => {
      try {
        setLoading(true);
        let id = null;
        if (processIdParam) {
          const parsed = Number(processIdParam);
          if (Number.isFinite(parsed) && parsed > 0) {
            id = parsed;
            try {
              localStorage.setItem("activeProcessId", String(parsed));
            } catch {}
          }
        }
        if (!Number.isFinite(id) || id <= 0) {
          const pathParts = String(location.pathname || "")
            .split("/")
            .filter(Boolean);
          const procIndex = pathParts.lastIndexOf("processes");
          if (procIndex >= 0) {
            const parsed = Number(pathParts[procIndex + 1]);
            if (Number.isFinite(parsed) && parsed > 0) {
              id = parsed;
              try {
                localStorage.setItem("activeProcessId", String(parsed));
              } catch {}
            }
          }
        }
        if (!Number.isFinite(id) || id <= 0) {
          try {
            id = Number(localStorage.getItem("activeProcessId"));
          } catch {}
        }
        if (!Number.isFinite(id) || id <= 0)
          throw new Error("No active process selected");
        setProcessId(id);

        const companiesRes = await getCompanyNames().catch(() => []);
        const proc = await getProcessWithRelations(id);
        setCompanies(Array.isArray(companiesRes) ? companiesRes : []);

        // Load process categories/statuses
        const [catsRes, statusRes] = await Promise.all([
          getProcessCategories().catch(() => []),
          getProcessStatuses().catch(() => []),
        ]);
        setProcessCategories(Array.isArray(catsRes) ? catsRes : []);
        setProcessStatuses(Array.isArray(statusRes) ? statusRes : []);

        // Load tasks for the process's company
        const procCompanyId =
          proc?.company_id || proc?.company?.company_id || "";
        if (procCompanyId) {
          const companyTasks = await getTasksByCompanyId(procCompanyId).catch(
            () => [],
          );
          setTasks(Array.isArray(companyTasks) ? companyTasks : []);
          const companyProcesses = await getProcessesByCompanyId(
            procCompanyId,
          ).catch(() => []);
          setProcesses(Array.isArray(companyProcesses) ? companyProcesses : []);
        } else {
          setTasks([]);
          setProcesses([]);
        }

        // Load KPIs for this process and available KPIs
        const [processKPIsRes, availableKPIsRes] = await Promise.all([
          getKPIsByProcessId(id).catch(() => []),
          getAllKPIs().catch(() => []),
        ]);
        setProcessKPIs(Array.isArray(processKPIsRes) ? processKPIsRes : []);
        setAvailableKPIs(
          Array.isArray(availableKPIsRes) ? availableKPIsRes : [],
        );

        if (proc) {
          setForm({
            process_name: proc.process_name || "",
            process_code: proc.process_code || "",
            version: proc.process_version ?? proc.version ?? 0,
            company_id: proc.company_id || proc.company?.company_id || "",
            process_status_id:
              proc.process_status_id ||
              proc.process_status?.id ||
              proc.processStatus?.id ||
              proc.status_id ||
              "",
            process_category_id:
              proc.process_category_id ||
              proc.process_category?.id ||
              proc.processCategory?.id ||
              proc.category_id ||
              "",
            parent_process_id: proc.parent_process_id || "",
            process_overview: proc.process_overview || "",
            case_arrival_rate: proc.case_arrival_rate != null ? String(proc.case_arrival_rate) : "",
          });
          const rows = Array.isArray(proc.process_tasks)
            ? [...proc.process_tasks]
            : [];
          rows.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
          setWorkflow(
            rows.map((pt) => {
              // Check if this is a child process or a task
              if (pt.child_process_id) {
                return {
                  isChildProcess: true,
                  child_process_id: pt.child_process_id || "",
                  task_id: "",
                  job_id: "",
                };
              } else {
                return {
                  task_id: pt.task_id || pt.task?.task_id || "",
                  job_id:
                    (pt.task?.jobTasks && pt.task.jobTasks[0]?.job?.job_id) ||
                    pt.job_id ||
                    "",
                };
              }
            }),
          );
          // Populate jobs chip display per row
          setWorkflowJobs(
            rows.map((pt) =>
              pt.child_process_id
                ? []
                : mapJobTasksToWorkflowJobs(pt.task?.jobTasks),
            ),
          );

          // Gateways (if provided by backend)
          const gws = Array.isArray(proc.gateways)
            ? proc.gateways.map((g) => {
                // Split the backend's flat branches[] into the editor's two
                // buckets. A branch is an *endpoint* (named / specific end event)
                // when it carries an endEventName or endTaskId and does NOT go to
                // a task. Everything else is a regular branch — including the
                // default "→ End" branch (no target, no end-event name), which
                // must be preserved so it survives a re-edit round-trip.
                const allBranches = Array.isArray(g.branches) ? g.branches : [];
                const firstVal = (...vals) => {
                  for (const v of vals) {
                    if (v != null && String(v) !== "") return String(v);
                  }
                  return "";
                };
                // A named End event now lives ON the branch (target = End Event
                // + a name); it stays a branch. Only "end after a specific task"
                // (endTaskId with no name) remains a legacy endpoint.
                const isEndpoint = (b) => {
                  const target = firstVal(b.targetTaskId, b.target_task_id);
                  const endName = (b.endEventName || b.end_event_name || "").trim();
                  const endTask = firstVal(b.endTaskId, b.end_task_id);
                  return target === "" && endTask !== "" && endName === "";
                };

                const regularBranches = allBranches
                  .filter((b) => !isEndpoint(b))
                  .map((b, idx) => ({
                    id: `${g.name || "gw"}-b-${idx}-${Math.random()
                      .toString(16)
                      .slice(2)}`,
                    _backendId: b.branch_pk_id ?? b.id ?? null,
                    targetTaskId: firstVal(b.targetTaskId, b.target_task_id),
                    // Raw target gateway pk (branch → gateway); resolved to
                    // "__GW__<localId>" in the post-pass once all local ids exist.
                    _targetGatewayPk:
                      b.target_gateway_id ?? b.targetGatewayId ?? null,
                    endEventName: b.endEventName || b.end_event_name || "",
                    condition: b.condition || "",
                    isDefault: !!(b.isDefault ?? b.is_default),
                    connectToEnd:
                      (b.connectToEnd ?? b.connect_to_end) !== false,
                  }));

                const endpoints = allBranches
                  .filter(isEndpoint)
                  .map((e, idx) => ({
                    id: `${g.name || "gw"}-e-${idx}-${Math.random()
                      .toString(16)
                      .slice(2)}`,
                    _backendId: e.branch_pk_id ?? e.id ?? null,
                    endTaskId: firstVal(e.endTaskId, e.end_task_id),
                    endEventName: e.endEventName || e.end_event_name || "",
                    condition: e.condition || "",
                  }));

                return {
                  id: `${g.name || g.type || "gw"}-${Math.random()
                    .toString(16)
                    .slice(2)}`,
                  _backendId: g.gateway_pk_id ?? g.id ?? null,
                  type: String(
                    g.type || g.gateway_type || "EXCLUSIVE",
                  ).toUpperCase(),
                  name: g.name || "",
                  afterTaskId:
                    g.afterTaskId != null
                      ? String(g.afterTaskId)
                      : g.after_task_id != null
                        ? String(g.after_task_id)
                        : START_EVENT_GATEWAY_ID,
                  convergeAtTaskId: (() => {
                    const raw =
                      g.convergeAtTaskId != null
                        ? g.convergeAtTaskId
                        : g.converge_at_task_id != null
                          ? g.converge_at_task_id
                          : "";
                    // Treat 0 / empty as unset so a bad backend default does not
                    // appear as a selected converge task in the dropdown.
                    if (raw === "" || raw == null || Number(raw) === 0) return "";
                    return String(raw);
                  })(),
                  convergeGatewayName:
                    g.convergeGatewayName || g.converge_gateway_name || "",
                  convergeToEnd: !!(g.convergeToEnd ?? g.converge_to_end),
                  // Raw target pk; resolved to the local convergeAtGatewayId
                  // (frontend id) in a post-pass once all ids exist.
                  _convergeAtGatewayPk:
                    g.convergeAtGatewayId ?? g.converge_at_gateway_id ?? null,
                  convergeAtGatewayId: "",
                  branches:
                    regularBranches.length > 0 || endpoints.length > 0
                      ? regularBranches
                      : [
                          {
                            id: `${Math.random()}`,
                            targetTaskId: "",
                            condition: "",
                          },
                          {
                            id: `${Math.random()}`,
                            targetTaskId: "",
                            condition: "",
                          },
                        ],
                  endpoints: endpoints,
                };
              })
            : [];
          // Resolve join → split references: map each gateway's target pk
          // (_convergeAtGatewayPk) to the local id of the gateway with that
          // _backendId, so the converge dropdown shows the selection.
          for (const gw of gws) {
            if (gw._convergeAtGatewayPk != null) {
              const target = gws.find(
                (x) => String(x._backendId) === String(gw._convergeAtGatewayPk),
              );
              if (target) gw.convergeAtGatewayId = target.id;
            }
            // Branch → gateway: map each branch's target gateway pk back to
            // "__GW__<localId>" so the branch dropdown shows the selection and it
            // round-trips. Without this a saved gateway-target branch reloads as
            // "End Event" even though it was saved correctly.
            for (const br of gw.branches || []) {
              if (br._targetGatewayPk != null) {
                const target = gws.find(
                  (x) => String(x._backendId) === String(br._targetGatewayPk),
                );
                if (target) br.targetTaskId = `__GW__${target.id}`;
              }
            }
          }
          setGateways(gws);
          originalGatewayBackendIdsRef.current = gws
            .map((g) => g._backendId)
            .filter(Boolean);
        }

        // Check if there's a task to add from the unassigned entities page
        try {
          const taskToAddId = localStorage.getItem("taskToAdd");
          if (taskToAddId) {
            const taskId = Number(taskToAddId);
            // Add the task to workflow if it's not already there
            setWorkflow((prev) => {
              const exists = prev.some((row) => Number(row.task_id) === taskId);
              if (!exists && taskId > 0) {
                return [...prev, { task_id: taskId, job_id: "" }];
              }
              return prev;
            });
            // Load jobs for this task
            try {
              const taskRel = await getTaskWithRelations(taskId);
              const jobsForTask = mapJobTasksToWorkflowJobs(taskRel?.jobTasks);
              setWorkflowJobs((prev) => [...prev, jobsForTask]);
            } catch {}
            // Clear the localStorage flag
            localStorage.removeItem("taskToAdd");
          }
        } catch {}
      } catch (e) {
        toast.error(e?.message || "Failed to load process for editing");
      } finally {
        setLoading(false);
      }
    })();
  }, [processIdParam, location.pathname]);

  const onBack = () => {
    const returnTo = localStorage.getItem("returnTo");
    if (returnTo === "unassigned-entities") {
      localStorage.removeItem("returnTo");
      setActiveSection("unassigned-entities");
    } else {
      setActiveSection("process-management");
    }
  };

  const handleChange = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // =====================
  // KPI Handlers
  // =====================
  const loadProcessKPIs = async () => {
    if (!processId) return;
    setKpiLoading(true);
    try {
      const data = await getKPIsByProcessId(processId);
      setProcessKPIs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load process KPIs:", e);
    } finally {
      setKpiLoading(false);
    }
  };

  const openAddKPIModal = () => {
    setKpiForm({
      kpiType: "TYPE1",
      kpi_id: availableKPIs.length > 0 ? availableKPIs[0].id : "",
      custom_kpi_name: "",
      custom_uom: "",
      value: "",
    });
    setKpiModalMode("create");
    setSelectedKPI(null);
    setShowKPIModal(true);
  };

  const openEditKPIModal = (kpi) => {
    setKpiForm({
      kpiType: kpi.kpiType || "TYPE1",
      kpi_id: kpi.kpi_id || "",
      custom_kpi_name: kpi.custom_kpi_name || "",
      custom_uom: kpi.custom_uom || "",
      value: kpi.value || "",
    });
    setKpiModalMode("edit");
    setSelectedKPI(kpi);
    setShowKPIModal(true);
  };

  const closeKPIModal = () => {
    setShowKPIModal(false);
    setSelectedKPI(null);
  };

  const handleKPIFormChange = (key, value) => {
    setKpiForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleKPISave = async () => {
    // Validation
    if (kpiForm.kpiType === "TYPE1") {
      if (!kpiForm.kpi_id) {
        toast.error("Please select a KPI");
        return;
      }
    } else {
      if (!kpiForm.custom_kpi_name.trim()) {
        toast.error("Custom KPI name is required");
        return;
      }
    }
    if (!kpiForm.value.trim()) {
      toast.error("Value is required");
      return;
    }

    setKpiSaving(true);
    try {
      if (kpiModalMode === "create") {
        await createProcessKPI({
          process_id: processId,
          kpiType: kpiForm.kpiType,
          kpi_id: kpiForm.kpiType === "TYPE1" ? kpiForm.kpi_id : undefined,
          custom_kpi_name:
            kpiForm.kpiType === "TYPE2" ? kpiForm.custom_kpi_name : undefined,
          custom_uom:
            kpiForm.kpiType === "TYPE2" ? kpiForm.custom_uom : undefined,
          value: kpiForm.value,
        });
        toast.success("KPI added successfully");
      } else if (selectedKPI) {
        await updateProcessKPI(selectedKPI.id, {
          value: kpiForm.value,
          kpiType: kpiForm.kpiType,
          kpi_id: kpiForm.kpiType === "TYPE1" ? kpiForm.kpi_id : undefined,
          custom_kpi_name:
            kpiForm.kpiType === "TYPE2" ? kpiForm.custom_kpi_name : undefined,
          custom_uom:
            kpiForm.kpiType === "TYPE2" ? kpiForm.custom_uom : undefined,
        });
        toast.success("KPI updated successfully");
      }
      await loadProcessKPIs();
      closeKPIModal();
    } catch (e) {
      toast.error(e?.message || "Failed to save KPI");
    } finally {
      setKpiSaving(false);
    }
  };

  const handleKPIDelete = async (kpi) => {
    if (!window.confirm(`Are you sure you want to remove this KPI?`)) return;
    try {
      await deleteProcessKPI(kpi.id);
      toast.success("KPI removed successfully");
      await loadProcessKPIs();
    } catch (e) {
      toast.error(e?.message || "Failed to remove KPI");
    }
  };

  const getKPIDisplayName = (kpi) => {
    if (kpi.kpiType === "TYPE2") {
      return kpi.custom_kpi_name || "Custom KPI";
    }
    return kpi.kpi?.kpiName || `KPI #${kpi.kpi_id}`;
  };

  const getKPIUoM = (kpi) => {
    if (kpi.kpiType === "TYPE2") {
      return kpi.custom_uom || "";
    }
    return kpi.kpi?.kpi_uom?.uomValue || "";
  };

  const addWorkflowRow = () => {
    setWorkflow((prev) => [...prev, { task_id: "", job_id: "" }]);
    setWorkflowJobs((prev) => [...(Array.isArray(prev) ? prev : []), []]);
  };
  const removeWorkflowRow = (index) => {
    setWorkflow((prev) => prev.filter((_, i) => i !== index));
    setWorkflowJobs((prev) =>
      Array.isArray(prev) ? prev.filter((_, i) => i !== index) : [],
    );
  };

  // --- Gateway helpers ---
  // Include workflow tasks AND child processes. Subprocess options use
  // child_process_id as the value (sent as afterTaskId/targetTaskId); the
  // backend resolves that against child_process_id — no special field needed.
  const workflowTaskOptions = React.useMemo(() => {
    return workflow
      .map((row, idx) => {
        if (row.isChildProcess) {
          const id = row.child_process_id;
          if (!id) return null;
          const cp = processes.find(
            (p) => String(p.process_id || p.id) === String(id),
          );
          const name = cp?.process_name || cp?.name;
          const code = cp?.process_code || cp?.code;
          const base = name
            ? `${name}${code ? ` [${code}]` : ""}`
            : `Process #${id}`;
          return {
            id: String(id),
            label: `${base} (Subprocess)`,
            order: idx + 1,
            isSubProcess: true,
          };
        }
        const id = row.task_id;
        if (!id) return null;
        const t = tasks.find((x) => String(x.task_id || x.id) === String(id));
        const label = t
          ? `${t.task_name || t.name}${
              t.task_code || t.code ? ` [${t.task_code || t.code}]` : ""
            }`
          : `Task #${id}`;
        return { id: String(id), label, order: idx + 1 };
      })
      .filter(Boolean);
  }, [workflow, tasks, processes]);

  const normalizeGatewayType = (type) =>
    String(type || "")
      .trim()
      .toUpperCase();

  // A converge (join) task is accepted for any merging gateway type so it is
  // persisted and rendered as a matching join (AND->AND, OR->OR, XOR->XOR).
  const needsConvergence = (type) => {
    const t = normalizeGatewayType(type);
    return (
      t === "PARALLEL" || t === "INCLUSIVE" || t === "EXCLUSIVE" || t === "COMPLEX"
    );
  };

  // Converge is mandatory for merging types, except Exclusive with End + branch(es).
  const requiresConvergence = (gatewayOrType) => {
    if (gatewayOrType && typeof gatewayOrType === "object") {
      return gatewayRequiresConvergence(gatewayOrType, needsConvergence);
    }
    return needsConvergence(gatewayOrType);
  };

  const getConvergeAtTaskId = (gateway) => {
    const raw = gateway?.convergeAtTaskId ?? gateway?.converge_at_task_id ?? "";
    if (raw === "" || raw == null || Number(raw) === 0) return "";
    return String(raw);
  };

  const getConvergeGatewayName = (gateway) =>
    gateway?.convergeGatewayName ?? gateway?.converge_gateway_name ?? "";

  const getConvergeToEnd = (gateway) =>
    !!(gateway?.convergeToEnd ?? gateway?.converge_to_end);

  const hasConvergeTarget = (gateway) =>
    !!(
      getConvergeAtTaskId(gateway) ||
      getConvergeToEnd(gateway) ||
      gateway?.convergeAtGatewayId
    );

  // When task changes, auto-select its first prelinked job if available
  const handleTaskSelect = async (index, taskId) => {
    setWorkflow((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, task_id: taskId, job_id: "" } : row,
      ),
    );
    const idNum = Number(taskId);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    const cached = (tasks || []).find(
      (t) => Number(t.task_id || t.id) === idNum,
    );
    let jobsForTask = mapJobTasksToWorkflowJobs(cached?.jobTasks);
    let firstJobId = jobsForTask[0]?.job_id ?? null;
    if (!jobsForTask.length) {
      try {
        const rel = await getTaskWithRelations(idNum);
        jobsForTask = mapJobTasksToWorkflowJobs(rel?.jobTasks);
        firstJobId = jobsForTask[0]?.job_id ?? null;
      } catch (_) {}
    }
    setWorkflowJobs((prev) => {
      const copy = Array.isArray(prev) ? [...prev] : [];
      copy[index] = jobsForTask;
      return copy;
    });
    if (firstJobId != null) {
      setWorkflow((prev) =>
        prev.map((row, i) =>
          i === index ? { ...row, job_id: String(firstJobId) } : row,
        ),
      );
    }
  };
  const moveWorkflowRowUp = (index) => {
    if (index <= 0) return;
    setWorkflow((prev) => {
      const copy = [...prev];
      const temp = copy[index - 1];
      copy[index - 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
    setWorkflowJobs((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const tmp = arr[index - 1];
      arr[index - 1] = arr[index];
      arr[index] = tmp;
      return arr;
    });
  };
  const moveWorkflowRowDown = (index) => {
    setWorkflow((prev) => {
      if (index >= prev.length - 1) return prev;
      const copy = [...prev];
      const temp = copy[index + 1];
      copy[index + 1] = copy[index];
      copy[index] = temp;
      return copy;
    });
    setWorkflowJobs((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (index >= arr.length - 1) return arr;
      const tmp = arr[index + 1];
      arr[index + 1] = arr[index];
      arr[index] = tmp;
      return arr;
    });
  };

  const validate = () => {
    if (!form.process_name.trim()) return "Process Name is required";
    if (!form.process_code.trim()) return "Process Code is required";
    if (!form.company_id) return "Company is required";
    const overviewText = String(form.process_overview || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    if (overviewText.length < 10)
      return "Process Overview must be at least 10 characters";
    if (!Array.isArray(workflow) || workflow.length === 0)
      return "At least one workflow row is required";
    for (const [i, r] of workflow.entries()) {
      // If it's a child process, only check child_process_id
      if (r.isChildProcess) {
        if (!r.child_process_id)
          return `Row ${i + 1}: Child Process is required`;
      } else {
        if (!r.task_id) return `Row ${i + 1}: Task is required`;
        if (!r.job_id) return `Row ${i + 1}: Job is required`;
      }
    }
    for (const [i, g] of gateways.entries()) {
      const type = normalizeGatewayType(g.type);
      const isParallel = type === "PARALLEL";
      const totalBranches = Array.isArray(g.branches) ? g.branches.length : 0;
      const totalEndpoints = Array.isArray(g.endpoints)
        ? g.endpoints.length
        : 0;
      const totalOutgoing = totalBranches + totalEndpoints;

      if (!g.afterTaskId) return `Gateway ${i + 1}: After Task is required`;

      if (type === "EXCLUSIVE" && totalOutgoing < 2)
        return `Gateway ${i + 1}: Exclusive gateways require at least two outgoing paths`;

      if (isParallel) {
        if (totalBranches < 2)
          return `Gateway ${i + 1}: Parallel gateways require at least two branches`;
        if (totalEndpoints > 0)
          return `Gateway ${i + 1}: Parallel gateways cannot have end events`;
      }

      if (requiresConvergence(g)) {
        if (!hasConvergeTarget(g))
          return `Gateway ${i + 1}: Join point is required (a task, End event, or another gateway)`;
      }

      if (!Array.isArray(g.branches) || g.branches.length === 0)
        return `Gateway ${i + 1}: At least one branch is required`;

      // A branch with a blank target is a valid path to the End Event, so it is
      // NOT an error for data-based gateways (exclusive / inclusive / complex /
      // event-based). Only PARALLEL requires every concurrent branch to reach a
      // concrete task.
      const missingTargets = (g.branches || []).filter((b) => !b.targetTaskId);
      if (isParallel && missingTargets.length > 0) {
        return `Gateway ${i + 1}, Branch ${
          (g.branches || []).indexOf(missingTargets[0]) + 1
        }: Target task is required for parallel gateways`;
      }

      // Two branches pointing at the same task/gateway is conflicting,
      // redundant routing — every outgoing path must go somewhere distinct.
      // A blank target (shared End event) is exempt.
      const targetCounts = new Map();
      (g.branches || []).forEach((b) => {
        if (!b.targetTaskId) return;
        const key = String(b.targetTaskId);
        targetCounts.set(key, (targetCounts.get(key) || 0) + 1);
      });
      const dupeIdx = (g.branches || []).findIndex(
        (b) => b.targetTaskId && targetCounts.get(String(b.targetTaskId)) > 1,
      );
      if (dupeIdx >= 0) {
        return `Gateway ${i + 1}, Branch ${
          dupeIdx + 1
        }: Another branch already routes to this same target`;
      }
    }
    return "";
  };

  const saveError = validate();
  const canSave = !saveError;

  const onSave = async () => {
    const v = validate();
    if (v) {
      toast.error(v);
      return;
    }
    try {
      setSaving(true);

      // Explicitly delete any gateways that were removed by the user.
      // The process PATCH endpoint does not remove gateways absent from its
      // payload, so we must call the dedicated DELETE endpoint for each one.
      const currentBackendIds = new Set(
        gateways.map((g) => g._backendId).filter(Boolean),
      );
      const removedBackendIds = originalGatewayBackendIdsRef.current.filter(
        (id) => !currentBackendIds.has(id),
      );
      for (const backendId of removedBackendIds) {
        try {
          await deleteGateway(backendId);
        } catch (e) {
          console.warn(`Failed to delete gateway ${backendId}:`, e);
        }
      }
      // Update the ref so a second save in the same session won't retry.
      originalGatewayBackendIdsRef.current = gateways
        .map((g) => g._backendId)
        .filter(Boolean);
      const payload = {
        process_name: form.process_name,
        process_code: form.process_code,
        version: form.version === "" ? 0 : Number(form.version),
        company_id: Number(form.company_id),
        process_status_id: form.process_status_id
          ? Number(form.process_status_id)
          : null,
        process_category_id: form.process_category_id
          ? Number(form.process_category_id)
          : null,
        parent_process_id: form.parent_process_id
          ? Number(form.parent_process_id)
          : null,
        process_overview: form.process_overview,
        case_arrival_rate: form.case_arrival_rate === "" ? null : Number(form.case_arrival_rate),
        workflow: workflow.map((r, idx) => {
          const item = { order: idx + 1 };
          if (r.isChildProcess) {
            item.item_type = "child_process";
            item.child_process_id = Number(r.child_process_id);
          } else {
            item.item_type = "task";
            item.task_id = Number(r.task_id);
            if (r.job_id) {
              item.job_id = Number(r.job_id);
            }
          }
          return item;
        }),
        gateways: gateways.map((g) => {
          const gwType = String(g.type || "").toUpperCase();
          const isParallel = gwType === "PARALLEL";
          const showConditions = [
            "EXCLUSIVE",
            "INCLUSIVE",
            "EVENT_BASED",
          ].includes(gwType);

          // Merge regular branches and endpoints into single branches array
          const regularBranches = (g.branches || []).map((b) => {
            const rawTarget = b.targetTaskId;
            // Branch → gateway: the target is "__GW__<localId>". Resolve it to the
            // target gateway's array position so the backend links target_gateway_id
            // (mirrors convergeAtGatewayIndex below). Without this the "__GW__…"
            // string would hit Number() → NaN and the gateway target would be lost.
            const isGatewayTarget =
              typeof rawTarget === "string" && rawTarget.startsWith("__GW__");
            const branch = {
              branch_pk_id:
                b._backendId != null && b._backendId !== ""
                  ? Number(b._backendId)
                  : undefined,
              targetTaskId: isGatewayTarget
                ? null
                : rawTarget
                  ? Number(rawTarget)
                  : null,
              isDefault: !!b.isDefault,
            };
            if (isGatewayTarget) {
              const ref = rawTarget.slice("__GW__".length);
              const gwIdx = gateways.findIndex(
                (x) => String(x.id) === String(ref),
              );
              if (gwIdx >= 0) branch.targetGatewayIndex = gwIdx;
            }
            if (showConditions) {
              branch.condition = b.condition || "";
            }
            if (!isGatewayTarget && b.targetTaskId) {
              // connect_to_end only applies to a branch that targets a task (a
              // chain). Persist the user's actual choice so unchecking it sticks.
              branch.connectToEnd = b.connectToEnd !== false;
            } else if (!isGatewayTarget && b.endEventName) {
              // Target = End Event with a name → this branch has its OWN named
              // end event; carry the name so it isn't lost on save.
              branch.endEventName = b.endEventName;
            }
            return branch;
          });

          const endpointBranches = isParallel
            ? []
            : (g.endpoints || []).map((e) => {
                const endpoint = {
                  branch_pk_id:
                    e._backendId != null && e._backendId !== ""
                      ? Number(e._backendId)
                      : undefined,
                  endTaskId: e.endTaskId ? Number(e.endTaskId) : null,
                  endEventName: e.endEventName || "",
                };
                if (showConditions) {
                  endpoint.condition = e.condition || "";
                }
                return endpoint;
              });

          const gateway = {
            gateway_pk_id:
              g._backendId != null && g._backendId !== ""
                ? Number(g._backendId)
                : undefined,
            type: (g.type || "").toUpperCase(),
            name: g.name || "",
            afterTaskId: normalizeGatewayAfterTaskId(g.afterTaskId),
            branches: [...regularBranches, ...endpointBranches],
          };

          const convergeAtTaskId = getConvergeAtTaskId(g);
          const convergeGatewayName = getConvergeGatewayName(g);
          const convergeToEnd = getConvergeToEnd(g);
          const skipConverge = exclusiveSkipsConvergence(g);

          // join → split: resolve the target gateway's local id to its array
          // position (the backend links join → split by array index).
          const gwTargetIdx =
            !skipConverge &&
            needsConvergence(g.type) &&
            g.convergeAtGatewayId
              ? gateways.findIndex(
                  (x) => String(x.id) === String(g.convergeAtGatewayId),
                )
              : -1;
          gateway.convergeAtGatewayIndex =
            gwTargetIdx >= 0 ? gwTargetIdx : undefined;
          const hasGwTarget = gateway.convergeAtGatewayIndex != null;

          gateway.convergeToEnd =
            !skipConverge &&
            !hasGwTarget &&
            needsConvergence(g.type) &&
            convergeToEnd;
          const convergeNum = Number(convergeAtTaskId);
          gateway.convergeAtTaskId =
            !skipConverge &&
            !hasGwTarget &&
            needsConvergence(g.type) &&
            !gateway.convergeToEnd &&
            convergeAtTaskId &&
            Number.isFinite(convergeNum) &&
            convergeNum > 0
              ? convergeNum
              : null;
          gateway.convergeGatewayName =
            !skipConverge &&
            needsConvergence(g.type) &&
            String(convergeGatewayName).trim()
              ? String(convergeGatewayName).trim()
              : null;

          return gateway;
        }),
      };
      await updateProcess(processId, payload);
      // The BPMN Editor caches a hand-laid-out XML diagram per process,
      // separate from this workflow/gateway data — invalidate it so the
      // editor regenerates fresh from what was just saved instead of
      // drifting out of sync with a stale cached diagram.
      await clearStoredBpmnXml(processId).catch(() => {});
      toast.success(
        `Process "${form.process_name}" has been updated successfully!`,
      );
      try {
        localStorage.setItem("processesNeedsRefresh", "1");
      } catch {}
      const returnTo = localStorage.getItem("returnTo");
      if (returnTo === "unassigned-entities") {
        localStorage.removeItem("returnTo");
        setActiveSection("unassigned-entities");
      } else {
        setActiveSection("process-management");
      }
    } catch (e) {
      const message = e?.message || "Failed to update process";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Edit Process</h2>
            <p className="page-subtitle">
              {form.process_name
                ? `Editing: ${form.process_name}`
                : "Update details and workflow, then save."}
              {form.process_code ? ` (${form.process_code})` : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button
              className="secondary-btn"
              onClick={onBack}
              disabled={saving}
            >
              Back
            </button>
            <button
              className="primary-btn"
              onClick={onSave}
              disabled={saving || !canSave}
              title={!canSave ? saveError : undefined}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ? (
          <div className="no-results">Loading...</div>
        ) : (
          <SideTabs
            defaultActiveId="basic"
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Process Name *</label>
                        <input
                          value={form.process_name}
                          onChange={(e) =>
                            handleChange("process_name", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Process Code *</label>
                        <input
                          value={form.process_code}
                          onChange={(e) =>
                            handleChange("process_code", e.target.value)
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Version</label>
                        <input
                          value={form.version ?? ""}
                          disabled
                          style={{
                            backgroundColor: "#f9fafb",
                            cursor: "not-allowed",
                            color: "#6b7280",
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Company *</label>
                        <select
                          value={form.company_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            handleChange("company_id", val);
                            loadTasksForCompany(val);
                            loadProcessesForCompany(val);
                            // Reset workflow and parent process when company changes
                            setWorkflow([]);
                            setWorkflowJobs([]);
                            handleChange("parent_process_id", "");
                          }}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: "#111827",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select company</option>
                          {companies.map((c) => (
                            <option
                              key={c.company_id || c.id}
                              value={c.company_id || c.id}
                            >
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Process Status</label>
                        <select
                          value={form.process_status_id}
                          onChange={(e) =>
                            handleChange("process_status_id", e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: "#111827",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select status</option>
                          {processStatuses.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.description || `Status ${s.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Process Category</label>
                        <select
                          value={form.process_category_id}
                          onChange={(e) =>
                            handleChange("process_category_id", e.target.value)
                          }
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: "#111827",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <option value="">Select category</option>
                          {processCategories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.description || `Category ${c.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Parent Process</label>
                        <select
                          value={form.parent_process_id}
                          onChange={(e) =>
                            handleChange("parent_process_id", e.target.value)
                          }
                          disabled={!form.company_id}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            fontSize: "14px",
                            color: !form.company_id ? "#9ca3af" : "#111827",
                            backgroundColor: !form.company_id
                              ? "#f9fafb"
                              : "#fff",
                            cursor: !form.company_id
                              ? "not-allowed"
                              : "pointer",
                          }}
                        >
                          <option value="">
                            {form.company_id
                              ? "Select parent process (optional)"
                              : "Select company first"}
                          </option>
                          {processes
                            .filter((p) => {
                              const processCompanyId =
                                p.company_id || p.company?.company_id;
                              const pId = p.process_id || p.id;
                              // Filter by company and exclude current process
                              return (
                                processCompanyId &&
                                String(processCompanyId) ===
                                  String(form.company_id) &&
                                Number(pId) !== processId
                              );
                            })
                            .map((p) => (
                              <option
                                key={p.process_id || p.id}
                                value={p.process_id || p.id}
                              >
                                {p.process_name || p.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <div className="form-group full">
                      <RichTextEditor
                        label="Process Overview *"
                        value={form.process_overview}
                        onChange={(html) =>
                          handleChange("process_overview", html)
                        }
                        height={220}
                      />
                    </div>
                    <div className="form-group" style={{ maxWidth: 360 }}>
                      <label htmlFor="case-arrival-rate">
                        Cases arriving per week
                      </label>
                      <input
                        id="case-arrival-rate"
                        type="number"
                        min="0"
                        step="0.1"
                        value={form.case_arrival_rate}
                        onChange={(e) => handleChange("case_arrival_rate", e.target.value)}
                        placeholder="e.g. 25"
                      />
                      <small style={{ color: "#6b7280" }}>
                        The process&apos;s demand (FBPM Ch.7 queueing input). Unlocks per-resource
                        utilization and queue-time estimates on the Process Optimization page.
                        Leave empty if unknown.
                      </small>
                    </div>
                  </div>
                ),
              },
              {
                id: "workflow",
                label: "Workflow",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{ marginBottom: 12, fontWeight: 600 }}
                    >
                      Tasks in Workflow
                    </div>
                    <RaciLegendBar />
                    <div style={{ overflowX: "auto" }}>
                      <div className="roles-table" style={{ width: "100%" }}>
                        <div
                          className="roles-table-header"
                          style={{ gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 60px auto" }}
                        >
                          <div className="cell">Task / Child Process</div>
                          <div className="cell">Jobs &amp; RACI</div>
                          <div className="cell">Order</div>
                          <div className="cell" style={{ textAlign: "right" }}>
                            Actions
                          </div>
                        </div>
                        {workflow.length === 0 ? (
                          <div className="no-results">No workflow rows.</div>
                        ) : (
                          workflow.map((row, idx) => (
                            <div
                              key={idx}
                              className="roles-table-row"
                              style={{
                                gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) 60px auto",
                              }}
                            >
                              <div className="cell">
                                {row.isChildProcess ? (
                                  <select
                                    style={{
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "1px solid #fcd34d",
                                      borderRadius: "6px",
                                      fontSize: "14px",
                                      color: "#92400e",
                                      background: "#fef3c7",
                                      cursor: "pointer",
                                    }}
                                    value={row.child_process_id || ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setWorkflow((prev) =>
                                        prev.map((r, i) =>
                                          i === idx
                                            ? { ...r, child_process_id: val }
                                            : r,
                                        ),
                                      );
                                    }}
                                  >
                                    <option value="">
                                      Select child process
                                    </option>
                                    {processes
                                      .filter((p) => {
                                        const processCompanyId =
                                          p.company_id || p.company?.company_id;
                                        const pId = p.process_id || p.id;
                                        return (
                                          processCompanyId &&
                                          String(processCompanyId) ===
                                            String(form.company_id) &&
                                          Number(pId) !== processId
                                        );
                                      })
                                      .map((cp) => (
                                        <option
                                          key={cp.process_id || cp.id}
                                          value={cp.process_id || cp.id}
                                        >
                                          {cp.process_name || cp.name}{" "}
                                          {cp.process_code || cp.code
                                            ? `- [${cp.process_code || cp.code}]`
                                            : ""}
                                        </option>
                                      ))}
                                  </select>
                                ) : (
                                  <select
                                    style={{
                                      width: "100%",
                                      padding: "8px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "14px",
                                      color: "#111827",
                                      backgroundColor: "#fff",
                                      cursor: "pointer",
                                    }}
                                    value={row.task_id}
                                    onChange={(e) =>
                                      handleTaskSelect(idx, e.target.value)
                                    }
                                  >
                                    <option value="">Select task</option>
                                    {tasks.map((t) => (
                                      <option
                                        key={t.task_id || t.id}
                                        value={t.task_id || t.id}
                                      >
                                        {t.task_name || t.name}{" "}
                                        {t.task_code || t.code
                                          ? `- [${t.task_code || t.code}]`
                                          : ""}
                                      </option>
                                    ))}
                                  </select>
                                )}
                              </div>
                              <div className="cell">
                                {row.isChildProcess ? (
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "4px 8px",
                                      background: "#fef3c7",
                                      color: "#92400e",
                                      borderRadius: 9999,
                                      fontSize: 12,
                                      border: "1px solid #fcd34d",
                                    }}
                                  >
                                    Child Process
                                  </span>
                                ) : (
                                  <WorkflowJobChips
                                    jobs={workflowJobs[idx]}
                                    taskId={row.task_id || null}
                                    showEditLink={!!row.task_id}
                                  />
                                )}
                              </div>
                              <div className="cell">
                                <span
                                  style={{
                                    display: "inline-block",
                                    minWidth: 28,
                                    textAlign: "center",
                                    padding: "4px 8px",
                                    background: "#f3f4f6",
                                    borderRadius: 6,
                                    border: "1px solid #e5e7eb",
                                  }}
                                >
                                  {idx + 1}
                                </span>
                              </div>
                              <div
                                className="cell"
                                style={{ textAlign: "right" }}
                              >
                                <div
                                  style={{
                                    display: "inline-flex",
                                    gap: 8,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <button
                                    className="secondary-btn sm"
                                    onClick={() => moveWorkflowRowUp(idx)}
                                    disabled={idx === 0}
                                  >
                                    ↑
                                  </button>
                                  <button
                                    className="secondary-btn sm"
                                    onClick={() => moveWorkflowRowDown(idx)}
                                    disabled={idx === workflow.length - 1}
                                  >
                                    ↓
                                  </button>
                                  <button
                                    className="danger-btn sm"
                                    onClick={() => removeWorkflowRow(idx)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                      <button
                        style={{
                          padding: "8px 16px",
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#1d4ed8";
                          e.target.style.boxShadow =
                            "0 2px 4px rgba(0,0,0,0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#2563eb";
                          e.target.style.boxShadow =
                            "0 1px 3px rgba(0,0,0,0.12)";
                        }}
                        onClick={addWorkflowRow}
                      >
                        + Add Task
                      </button>
                      <button
                        style={{
                          padding: "8px 16px",
                          background: form.company_id ? "#059669" : "#9ca3af",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: form.company_id ? "pointer" : "not-allowed",
                          transition: "all 0.2s",
                          boxShadow: form.company_id
                            ? "0 1px 3px rgba(0,0,0,0.12)"
                            : "none",
                        }}
                        onMouseEnter={(e) => {
                          if (form.company_id) {
                            e.target.style.background = "#047857";
                            e.target.style.boxShadow =
                              "0 2px 4px rgba(0,0,0,0.15)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (form.company_id) {
                            e.target.style.background = "#059669";
                            e.target.style.boxShadow =
                              "0 1px 3px rgba(0,0,0,0.12)";
                          }
                        }}
                        onClick={() => {
                          if (form.company_id) {
                            // Add a child process row with special type marker
                            setWorkflow((prev) => [
                              ...prev,
                              {
                                task_id: "",
                                job_id: "",
                                isChildProcess: true,
                                child_process_id: "",
                              },
                            ]);
                            setWorkflowJobs((prev) => [
                              ...(Array.isArray(prev) ? prev : []),
                              [],
                            ]);
                          }
                        }}
                        disabled={!form.company_id}
                        title={
                          !form.company_id
                            ? "Select a company first to add child processes"
                            : "Add a child process to workflow"
                        }
                      >
                        + Add Child Process
                      </button>
                    </div>
                    {!form.company_id && (
                      <p
                        style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}
                      >
                        Select a company first to add child processes.
                      </p>
                    )}

                    {/* New Gateway Section Component */}
                    <GatewaySection
                      gateways={gateways}
                      onGatewaysChange={setGateways}
                      workflowTasks={workflowTaskOptions.map((opt, idx) => ({
                        task_id: opt.id,
                        task_name: opt.label,
                        order: opt.order || idx + 1,
                        isSubProcess: !!opt.isSubProcess,
                      }))}
                    />
                  </div>
                ),
              },
              {
                id: "kpis",
                label: "KPIs",
                content: (
                  <div className="role-card">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                      }}
                    >
                      <div
                        className="section-title"
                        style={{ fontWeight: 600 }}
                      >
                        Process KPIs
                      </div>
                      <button className="primary-btn" onClick={openAddKPIModal}>
                        + Add KPI
                      </button>
                    </div>

                    {kpiLoading ? (
                      <div className="no-results">Loading KPIs...</div>
                    ) : processKPIs.length === 0 ? (
                      <div className="no-results">
                        <p>No KPIs linked to this process yet.</p>
                        <button
                          className="primary-btn"
                          onClick={openAddKPIModal}
                          style={{ marginTop: 12 }}
                        >
                          Add your first KPI
                        </button>
                      </div>
                    ) : (
                      <div className="kpi-table-container">
                        <table className="kpi-table">
                          <thead>
                            <tr>
                              <th>KPI Name</th>
                              <th>Type</th>
                              <th>Value</th>
                              <th>Unit</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {processKPIs.map((kpi) => (
                              <tr key={kpi.id}>
                                <td className="kpi-name-cell">
                                  {getKPIDisplayName(kpi)}
                                </td>
                                <td>
                                  <span
                                    className={`type-badge type-${kpi.kpiType?.toLowerCase()}`}
                                  >
                                    {kpi.kpiType === "TYPE2"
                                      ? "Custom"
                                      : "Standard"}
                                  </span>
                                </td>
                                <td>
                                  <span className="value-badge">
                                    {kpi.value}
                                  </span>
                                </td>
                                <td>
                                  {getKPIUoM(kpi) ? (
                                    <span className="uom-badge">
                                      {getKPIUoM(kpi)}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#9ca3af" }}>-</span>
                                  )}
                                </td>
                                <td className="actions-cell">
                                  <button
                                    className="action-btn edit"
                                    onClick={() => openEditKPIModal(kpi)}
                                    title="Edit"
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="action-btn delete"
                                    onClick={() => handleKPIDelete(kpi)}
                                    title="Delete"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ),
              },
              {
                id: "value-added",
                label: "Value-Added",
                content: (
                  <div className="role-card">
                    <ValueAddedPanel processId={processId || processIdParam} />
                  </div>
                ),
              },
              {
                id: "performance",
                label: "Performance",
                content: (
                  <div className="role-card">
                    <PerformancePanel processId={processId || processIdParam} />
                  </div>
                ),
              },
              {
                id: "flow-analysis",
                label: "Flow Analysis",
                content: (
                  <div className="role-card">
                    <FlowAnalysisPanel processId={processId || processIdParam} />
                  </div>
                ),
              },
              {
                id: "redesign-scenarios",
                label: "Redesign Scenarios",
                content: (
                  <div className="role-card">
                    <RedesignScenariosPanel processId={processId || processIdParam} />
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>

      {/* KPI Modal */}
      {showKPIModal && (
        <div className="modal-overlay" onClick={closeKPIModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {kpiModalMode === "create"
                  ? "Add KPI to Process"
                  : "Edit Process KPI"}
              </h3>
              <button className="modal-close" onClick={closeKPIModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>KPI Type</label>
                <select
                  value={kpiForm.kpiType}
                  onChange={(e) =>
                    handleKPIFormChange("kpiType", e.target.value)
                  }
                >
                  <option value="TYPE1">Standard (Select from KPI list)</option>
                  <option value="TYPE2">Custom (Free entry)</option>
                </select>
              </div>

              {kpiForm.kpiType === "TYPE1" ? (
                <div className="form-group">
                  <label>Select KPI *</label>
                  <select
                    value={kpiForm.kpi_id}
                    onChange={(e) =>
                      handleKPIFormChange("kpi_id", e.target.value)
                    }
                  >
                    <option value="">Select a KPI</option>
                    {availableKPIs.map((kpi) => (
                      <option key={kpi.id} value={kpi.id}>
                        {kpi.kpiName}
                        {kpi.kpi_uom?.uomValue && ` (${kpi.kpi_uom.uomValue})`}
                      </option>
                    ))}
                  </select>
                  {availableKPIs.length === 0 && (
                    <p className="form-hint">
                      No KPIs available. Create KPIs in KPI Management first.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Custom KPI Name *</label>
                    <input
                      type="text"
                      value={kpiForm.custom_kpi_name}
                      onChange={(e) =>
                        handleKPIFormChange("custom_kpi_name", e.target.value)
                      }
                      placeholder="Enter custom KPI name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Custom Unit of Measure</label>
                    <input
                      type="text"
                      value={kpiForm.custom_uom}
                      onChange={(e) =>
                        handleKPIFormChange("custom_uom", e.target.value)
                      }
                      placeholder="e.g., %, hours, units"
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Value *</label>
                <input
                  type="text"
                  value={kpiForm.value}
                  onChange={(e) => handleKPIFormChange("value", e.target.value)}
                  placeholder="Enter value"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button className="cancel-btn" onClick={closeKPIModal}>
                Cancel
              </button>
              <button
                className="submit-btn"
                onClick={handleKPISave}
                disabled={kpiSaving}
              >
                {kpiSaving
                  ? "Saving..."
                  : kpiModalMode === "create"
                    ? "Add KPI"
                    : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessEditPage;
